import { toast } from 'react-toastify'
import { cleanObject, getErrorMessage } from '../../helpers'
import { BaseApiResponse, QueryProps } from '../shared/shared.type'
import { apiSlice } from '../api/apiSlice'
import { updateAdminCart } from '../auth/auth.reducer'
import { ReportPeriodRange } from '../reports/reports.type'
import { ErrorType } from '../shared/shared.type'
import {
  type OrderMetricsCount,
  type OrderMetricsValue,
  type OrderResponse,
  type OrderResponsePayload,
  CreatorMetricsResponse,
  HarmonizedOrdersBreakdownResponse,
  HarmonizedOrdersResponse,
  OrderChannelStatus,
  OrderQueryProps,
  OrderStatus,
  SingleOrder,
  UpdateOrderStatusResponse,
} from './orders.type'
import {
  AddItemToCart,
  CartResponse,
  InitiateOrderPayment,
  InitiateOrderPaymentResponse,
  PlaceOrderPayload,
  RemoveItemFromCart,
  SingleCart,
  UpdateCartItemQuantity,
} from '../cart/cart.type'
import {
  OrderSettlementsByHarmonizedRefResponse,
  OrderSettlementsResponse,
  RetrySettlementResponse,
  SettlementsResponse,
  SingleSettlementsResponse,
} from '../settlements/settlements.type'

type MainOrderResponse = {
  data: OrderResponsePayload
}

export const orderApiSlice = apiSlice.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getOrders: builder.query<
      MainOrderResponse,
      QueryProps & { status?: string; startDate?: string; endDate?: string }
    >({
      query: ({ limit, page, status, startDate, endDate, search }) => ({
        url: `v1/order/all`,
        method: 'GET',
        params: {
          sortDirection: 'desc',
          sortField: 'date',
          page: page,
          limit: limit,
          status: status,
          startDate: startDate,
          endDate: endDate,
          filter: search,
        },
      }),
      providesTags: [{ type: 'ORDER', id: 'LIST' }],
    }),
    getHarmonizedOrders: builder.query<
      HarmonizedOrdersResponse,
      OrderQueryProps
    >({
      query: ({ page, ...params }) => ({
        url: `v1/order/admin/all`,
        method: 'GET',
        params: {
          ...params,
          sortDirection: 'desc',
          sortField: 'date',
          skip: (Number(page) - 1) * Number(params.limit),
        },
      }),
      providesTags: [{ type: 'ORDER', id: 'LIST' }],
    }),
    getHarmonizedOrdersBreakdown: builder.query<
      HarmonizedOrdersBreakdownResponse,
      { harmonizedReference: string; fulfilmentCentre?: string }
    >({
      query: ({ harmonizedReference, fulfilmentCentre }) => ({
        url: `v1/order/admin/harmonized-reference/${harmonizedReference}`,
        params: { fulfilmentCentre },
        method: 'GET',
      }),
      providesTags: (_result, _error, arg) => [
        { type: 'ORDER', id: arg.harmonizedReference },
      ],
    }),
    getPOSOrderForResume: builder.query<
      { success: boolean; data: { orders: SingleOrder[]; cart: SingleCart } },
      { harmonizedReference: string; fulfilmentCentre?: string }
    >({
      query: ({ harmonizedReference, fulfilmentCentre }) => ({
        url: `v1/order/admin/harmonized-reference/${harmonizedReference}/resume`,
        params: { fulfilmentCentre },
      }),
      providesTags: (_result, _error, arg) => [
        { type: 'ORDER', id: arg.harmonizedReference },
        { type: 'CART', id: 'LIST' },
      ],
    }),
    getOrdersMetricsCount: builder.query<
      OrderMetricsCount,
      {
        search?: string
        startDate?: string
        endDate?: string
        fulfilmentCentre?: string
        vendorId?: string
        creatorType?: OrderQueryProps['creatorType']
        isCustomDateRange?: boolean
        filter?: ReportPeriodRange
        status?: string
        channel?: OrderChannelStatus
      }
    >({
      query: (params) => ({
        url: `v1/order/metrics/count`,
        method: 'GET',
        params: params,
      }),
      providesTags: ['ORDER_METRICS'],
    }),
    getOrdersMetricsValue: builder.query<
      OrderMetricsValue,
      {
        search?: string
        startDate?: string
        endDate?: string
        fulfilmentCentre?: string
        vendorId?: string
        creatorType?: OrderQueryProps['creatorType']
        isCustomDateRange?: boolean
        filter?: ReportPeriodRange
        status?: string
        channel?: OrderChannelStatus
      }
    >({
      query: (params) => ({
        url: `v1/order/metrics/value`,
        method: 'GET',
        params: params,
      }),
      providesTags: ['ORDER_METRICS'],
    }),
    getTopCreatorsByOrders: builder.query<CreatorMetricsResponse, void>({
      query: () => ({
        url: `v1/order/metrics/best-seller/order-count`,
        method: 'GET',
        params: {},
      }),
      providesTags: ['ORDER_METRICS'],
    }),
    getTopCreatorsBySales: builder.query<CreatorMetricsResponse, void>({
      query: () => ({
        url: `v1/order/metrics/best-seller/sale-value`,
        method: 'GET',
        params: {},
      }),
      providesTags: ['ORDER_METRICS'],
    }),
    getOrder: builder.query<{ data: OrderResponse }, { id: string }>({
      query: ({ id }) => ({
        url: `v1/order/admin/${id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, arg) => [{ type: 'ORDER', id: arg.id }],
    }),
    updateOrder: builder.mutation<
      UpdateOrderStatusResponse,
      { body: { status: OrderStatus }; id: string }
    >({
      query: (payload) => ({
        url: `v1/order/${payload.id}/status`,
        method: 'PATCH',
        body: payload.body,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'ORDER', id: arg.id },
        { type: 'ORDER', id: 'LIST' },
        'ORDER_METRICS',
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const data = await queryFulfilled
          toast.success('Order updated successfully')
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    requestOrderDelivery: builder.mutation<OrderResponse, { id: string }>({
      query: ({ id }) => ({
        url: `v1/order/${id}/request-delivery`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'ORDER', id: arg.id },
        { type: 'ORDER', id: 'LIST' },
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const data = await queryFulfilled
          toast.success('Delivery requested successfully')
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    getOrderSettlements: builder.query<
      OrderSettlementsResponse,
      { id: string }
    >({
      query: ({ id }) => ({
        url: `v1/order/${id}/settlements`,
        method: 'GET',
      }),
      providesTags: [{ type: 'SETTLEMENT', id: 'LIST' }],
    }),
    getOrderSettlementsByHarmonizedRef: builder.query<
      OrderSettlementsByHarmonizedRefResponse,
      { ref: string; page?: number; limit?: number; fulfilmentCentre?: string }
    >({
      query: ({ ref, page, limit, fulfilmentCentre }) => ({
        url: `v1/settlements/harmonized-reference`,
        method: 'GET',
        params: {
          reference: ref,
          fulfilmentCentre,
          page,
          limit,
        },
      }),
      providesTags: [{ type: 'SETTLEMENT', id: 'LIST' }],
    }),
    getSettlements: builder.query<
      SettlementsResponse,
      QueryProps & { party?: string; fulfilmentCentre?: string }
    >({
      query: (payload) => {
        const params = cleanObject(payload)

        return {
          url: `v1/settlements`,
          method: 'GET',
          params: {
            ...params,
          },
        }
      },
      providesTags: [{ type: 'SETTLEMENT', id: 'LIST' }],
    }),
    getSingleSettlement: builder.query<
      SingleSettlementsResponse,
      { id: string }
    >({
      query: ({ id }) => ({
        url: `v1/settlements/${id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, arg) => [
        { type: 'SETTLEMENT', id: arg.id },
      ],
    }),
    retrySettlement: builder.mutation<
      RetrySettlementResponse,
      { id: string; password: string }
    >({
      query: ({ id, password }) => ({
        url: `v1/settlements/${id}/retry`,
        method: 'POST',
        body: {
          password,
        },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'SETTLEMENT', id: arg.id },
        { type: 'SETTLEMENT', id: 'LIST' },
        'SETTLEMENT',
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const data = await queryFulfilled
          toast.success(data.data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    createCart: builder.mutation<CartResponse, void>({
      query: () => ({
        url: `v2/carts/admin`,
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'CART', id: 'LIST' }],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const resp = await queryFulfilled
          if (resp.data.success) {
            dispatch(
              updateAdminCart({
                adminCart: resp.data.data.cart,
                adminCartItems: resp.data.data.cartItems,
              }),
            )
          }
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    getCart: builder.query<CartResponse, string>({
      query: (id) => ({
        url: `v2/carts/admin/${id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, arg) => [{ type: 'CART', id: arg }],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const resp = await queryFulfilled
          if (resp.data.success) {
            dispatch(
              updateAdminCart({
                adminCart: resp.data.data.cart,
                adminCartItems: resp.data.data.cartItems,
              }),
            )
          }
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    addItemToCart: builder.mutation<
      CartResponse,
      { id: string; payload: AddItemToCart; fulfillmentcenterId?: string }
    >({
      query: ({ id, payload, fulfillmentcenterId }) => ({
        url: `v2/carts/admin/${id}/item`,
        method: 'POST',
        body: payload,
        headers: {
          fulfillmentcenterId: fulfillmentcenterId ?? '',
        },
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'CART', id: arg.id }],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const resp = await queryFulfilled
          if (resp.data.success) {
            dispatch(
              updateAdminCart({
                adminCart: resp.data.data.cart,
                adminCartItems: resp.data.data.cartItems,
              }),
            )
          }
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    removeItemFromCart: builder.mutation<
      CartResponse,
      { id: string; payload: RemoveItemFromCart }
    >({
      query: ({ id, payload }) => ({
        url: `v2/carts/admin/${id}/item`,
        method: 'DELETE',
        body: payload,
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'CART', id: arg.id }],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const resp = await queryFulfilled
          if (resp.data.success) {
            dispatch(
              updateAdminCart({
                adminCart: resp.data.data.cart,
                adminCartItems: resp.data.data.cartItems,
              }),
            )
          }
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    updateCartItemQuantity: builder.mutation<
      CartResponse,
      { id: string; payload: UpdateCartItemQuantity }
    >({
      query: ({ id, payload }) => ({
        url: `v2/carts/admin/${id}/item`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'CART', id: arg.id }],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const resp = await queryFulfilled
          if (resp.data.success) {
            dispatch(
              updateAdminCart({
                adminCart: resp.data.data.cart,
                adminCartItems: resp.data.data.cartItems,
              }),
            )
          }
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    createOrder: builder.mutation<
      BaseApiResponse & { data: SingleOrder },
      { payload: PlaceOrderPayload }
    >({
      query: ({ payload }) => ({
        url: `v2/orders/admin`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [{ type: 'ORDER', id: 'LIST' }, 'ORDER_METRICS'],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    initiateOrderPayment: builder.mutation<
      InitiateOrderPaymentResponse,
      { reference: string; payload: InitiateOrderPayment }
    >({
      query: ({ reference, payload }) => ({
        url: `v2/orders/admin/${reference}/pay`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'ORDER', id: arg.reference },
        { type: 'ORDER', id: 'LIST' },
        'ORDER_METRICS',
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
  }),
})

export const {
  useGetOrderQuery,
  useGetOrdersQuery,
  useCreateOrderMutation,
  useUpdateOrderMutation,
  useGetOrdersMetricsCountQuery,
  useGetOrdersMetricsValueQuery,
  useRequestOrderDeliveryMutation,
  useGetTopCreatorsByOrdersQuery,
  useGetTopCreatorsBySalesQuery,
  useGetOrderSettlementsQuery,
  useGetOrderSettlementsByHarmonizedRefQuery,
  useGetSettlementsQuery,
  useGetSingleSettlementQuery,
  useRetrySettlementMutation,
  useGetHarmonizedOrdersQuery,
  useGetHarmonizedOrdersBreakdownQuery,
  useLazyGetHarmonizedOrdersBreakdownQuery,
  useLazyGetPOSOrderForResumeQuery,
  useCreateCartMutation,
  useGetCartQuery,
  useAddItemToCartMutation,
  useRemoveItemFromCartMutation,
  useUpdateCartItemQuantityMutation,
  useInitiateOrderPaymentMutation,
} = orderApiSlice
